import type { CSSProperties, HTMLAttributes, ReactElement } from 'react';
import { cloneElement, forwardRef } from 'react';
import styled from 'styled-components';
import type {
  LanguageCode,
  TextStyleValue
} from '@elfsight/configurator-contracts';
import type { TextStyleMixin } from '@elfsight/widget-ui';
import {
  Typography,
  textColorMixin,
  textStyleMixin
} from '@elfsight/widget-ui';
import { createReadableClass, roundTo4 } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import type { FieldType } from '../contexts';
import { commonFieldsSettings, LINE_HEIGHT_RATIO } from '../fields';
import type { Country } from '../fields/phone/countries';

const FIELDS_WITHOUT_HTML_LABEL = [
  'label',
  'starScale',
  'numberScale',
  'thumbScale',
  'smileyScale',
  'dropdown',
  'imageChoice',
  'choice'
];

export type FormFieldLayoutProps = HTMLAttributes<HTMLElement> & {
  children: ReactElement;
  id: string;
  type: FieldType;
  required: boolean;
  showLabel?: boolean;
  name?: string;
  label?: string;
  placeholder?: string;
  hint?: string;
  columns?: [number, number];
  countries?: Country[];
  errorColor?: string;
  invalidError?: boolean;
  requiredError?: boolean;
  highlightError?: boolean;
  invalidErrorText?: string;
  requiredErrorText?: string;

  fieldActiveColor?: string;
  fieldColor?: string;
  fieldFontStyle?: TextStyleValue;
  fieldFillColor?: string;

  fieldBorderType?: 'outline' | 'underline' | null;
  fieldBorderColor?: string;
  fieldBorderWidth?: number;
  fieldBorderRadius?: number;

  fieldHintInsideTypes?: FieldType[];
  fieldHintColor?: string;
  fieldHintFontStyle?: TextStyleValue;

  fieldLabelPosition?: 'inside' | 'outside';
  fieldLabelOnlyOutside?: boolean;
  fieldLabelOutsideStyle?: boolean;
  fieldLabelColor?: string;
  fieldInsideLabelColor?: string;
  fieldLabelFontStyle: TextStyleValue;
  fieldInsideLabelFontStyle: TextStyleValue;
  hideErrors?: boolean;
  isHorizontal?: boolean;
  isOutside?: boolean;
  style?: CSSProperties;

  invalidErrorMessage?: string;
  requiredErrorMessage?: string;

  fieldSpacing?: number;
  fieldPadding?: number;

  addBottomMarginToLastChild?: boolean;

  widgetLanguage?: LanguageCode;
};

export const FormFieldLayout = forwardRef<HTMLDivElement, FormFieldLayoutProps>(
  function FormFieldLayout(
    {
      children,
      id,
      type,
      name,
      label,
      hint,
      required,
      showLabel = true,
      columns,
      invalidError,
      requiredError,
      highlightError,
      errorColor,

      fieldActiveColor = commonFieldsSettings.fieldActiveColor,
      fieldColor = commonFieldsSettings.fieldColor,
      fieldFontStyle = commonFieldsSettings.fieldFontStyle,

      fieldHintInsideTypes = ['choice'],
      fieldHintColor = commonFieldsSettings.fieldHintColor,
      fieldHintFontStyle = commonFieldsSettings.fieldHintFontStyle,

      fieldLabelPosition = commonFieldsSettings.fieldLabelPosition,
      fieldLabelOnlyOutside,
      fieldLabelOutsideStyle,
      fieldLabelFontStyle = commonFieldsSettings.fieldLabelFontStyle,
      fieldInsideLabelFontStyle = commonFieldsSettings.fieldInsideLabelFontStyle,
      fieldLabelColor = commonFieldsSettings.fieldLabelColor,
      fieldInsideLabelColor = commonFieldsSettings.fieldInsideLabelColor,
      hideErrors = false,
      isHorizontal = false,
      isOutside = false,
      style,

      invalidErrorMessage: derivedInvalidErrorMessage,
      requiredErrorMessage: derivedRequiredErrorMessage,

      fieldSpacing = commonFieldsSettings.fieldSpacing,
      fieldPadding,
      className,
      addBottomMarginToLastChild = false,
      ...forwardingProps
    },
    forwardingRef
  ) {
    const { fontWeight, fontStyle } = fieldFontStyle;
    const isOutsideLabel = fieldLabelPosition === 'outside';
    const fullLabel = label && `${label}${required ? ' *' : ''}`;
    const hintBelow = !fieldHintInsideTypes.includes(type);

    const labelMargin = fieldLabelOnlyOutside && hint && !hintBelow ? 4 : 8;
    const labelLineHeight = roundTo4(
      fieldLabelFontStyle.fontSize * LINE_HEIGHT_RATIO
    );
    const bottomMargin = type === 'label' || isHorizontal ? 0 : fieldSpacing;
    const topMargin = isOutside ? fieldSpacing : 0;
    const padding = fieldPadding ?? fieldSpacing / 2;

    const labelColor =
      isOutsideLabel || fieldLabelOnlyOutside || fieldLabelOutsideStyle
        ? fieldLabelColor
        : fieldInsideLabelColor;
    const labelFontStyle =
      isOutsideLabel || fieldLabelOnlyOutside || fieldLabelOutsideStyle
        ? fieldLabelFontStyle
        : fieldInsideLabelFontStyle;

    const { t } = useLingui();

    const invalidErrorMessage =
      derivedInvalidErrorMessage ?? t`Please enter a valid ${name}`;
    const requiredErrorMessage =
      derivedRequiredErrorMessage ?? t`This field is required`;

    const hasLabel =
      showLabel &&
      type !== 'hidden' &&
      !!label &&
      (isOutsideLabel || fieldLabelOnlyOutside);

    // avoid "Orphaned form label" error
    const canHaveActualLabel = !FIELDS_WITHOUT_HTML_LABEL.includes(type);

    return (
      <Container
        ref={forwardingRef}
        className={className}
        _color={fieldColor}
        _columns={columns}
        _bottomMargin={bottomMargin}
        _topMargin={topMargin}
        _padding={padding}
        _hidden={type === 'hidden'}
        _addBottomMarginToLastChild={addBottomMarginToLastChild}
        _headerHint={!hintBelow}
      >
        {hasLabel && (
          <Label
            as={canHaveActualLabel ? 'label' : 'div'}
            htmlFor={canHaveActualLabel ? id : undefined}
            _textColor={labelColor}
            _textStyle={labelFontStyle}
            _labelLineHeight={labelLineHeight}
            _labelMargin={labelMargin}
          >
            {fullLabel}
          </Label>
        )}

        {!!hint && !hintBelow && (
          <Hint
            _textColor={fieldHintColor}
            _textStyle={fieldHintFontStyle}
            dangerouslySetInnerHTML={{ __html: hint }}
            _headerHint
          />
        )}

        <Element _isHeading={type === 'heading'}>
          {cloneElement(children, {
            ...children.props,
            ...forwardingProps,
            id,
            required,
            showLabel: !['imageChoice', 'dropdown'].includes(type) && showLabel,
            label: fullLabel,
            hint,

            fieldActiveColor,
            fieldColor,
            fieldFontStyle,

            fieldHintColor,
            fieldHintFontStyle,

            fieldLabelPosition,
            fieldLabelColor: labelColor,
            fieldLabelFontStyle: labelFontStyle,

            fieldPlaceholderFontStyle: fontStyle,
            fieldPlaceholderFontWeight: fontWeight,

            fieldSpacing,
            highlightError,
            customVariant: highlightError ? 'error' : undefined,
            style: {
              ...style,
              height: 'max-content'
            }
          })}

          {!hideErrors && invalidError && (
            <Error _color={errorColor}>{invalidErrorMessage}</Error>
          )}
          {!hideErrors && requiredError && (
            <Error _color={errorColor}>{requiredErrorMessage}</Error>
          )}

          {!!hint && hintBelow && (
            <Hint
              _textColor={fieldHintColor}
              _textStyle={fieldHintFontStyle}
              dangerouslySetInnerHTML={{ __html: hint }}
            />
          )}
        </Element>
      </Container>
    );
  }
);

type TextStyle = {
  _textColor: string;
  _textStyle: TextStyleMixin;
};

const Label = styled.label.attrs<
  TextStyle & {
    _labelLineHeight?: number;
    _labelMargin?: number;
  }
>({
  className: createReadableClass({
    component: 'field-layout',
    element: 'label'
  })
})`
  display: flex;
  align-items: flex-end;
  grid-row: 1/1;
  line-height: ${({ _labelLineHeight }) => _labelLineHeight}px;
  margin-bottom: ${({ _labelMargin }) => _labelMargin}px;
  ${textStyleMixin}
  ${textColorMixin}
`;

/* TODO: refactor heading specific styles */
const Element = styled.div.attrs<{ _isHeading?: boolean }>({
  className: createReadableClass({
    component: 'field-layout',
    element: 'element'
  })
})`
  ${({ _isHeading }) =>
    _isHeading
      ? `
    grid-template-rows: subgrid;
    display: grid;
    grid-row: span 2;
  `
      : `
    grid-row: 3/3;
    min-width: 0;
  `}
`;

const Error = styled(Typography).attrs<{ _color?: string }>(
  ({ _color = 'rgb(255, 56, 56)' }) => ({
    className: createReadableClass({
      component: 'field-layout',
      element: 'error'
    }),
    color: _color,
    textStyle: {
      fontSize: 13
    }
  })
)``;

const Hint = styled.div.attrs<TextStyle & { _headerHint?: boolean }>({
  className: createReadableClass({
    component: 'field-layout',
    element: 'hint'
  })
})`
  font-size: 13px;
  line-height: 1.2;
  ${({ _headerHint }) => (_headerHint ? 'grid-row: 2/2;' : '')}
  ${textStyleMixin}
  ${textColorMixin}
`;

const Container = styled.div.attrs<{
  _color: string;
  _columns?: [number, number];
  _bottomMargin: number;
  _topMargin: number;
  _padding: number;
  _hidden: boolean;
  _addBottomMarginToLastChild: boolean;
  _headerHint?: boolean;
}>({
  className: createReadableClass({
    component: 'field-layout',
    element: 'container'
  })
})`
  ${({
    _color,
    _columns,
    _bottomMargin,
    _topMargin,
    _padding,
    _hidden,
    _addBottomMarginToLastChild,
    _headerHint
  }) => `
    box-sizing: border-box;
    text-align: left;
    display: ${_hidden ? 'none' : 'grid'};
    grid-template-rows: subgrid;
    grid-row: span 3;
    ${
      _columns
        ? `
      grid-column: ${_columns[0]}/${_columns[1]};
    `
        : ''
    }
    padding: 0 ${_padding}px;
    ${
      _addBottomMarginToLastChild
        ? `margin-bottom: ${_bottomMargin}px;`
        : `&:not(:last-child) {
        margin-bottom: ${_bottomMargin}px;
      }`
    }
    margin-top: ${_topMargin}px;

    ${Hint} {
      ${_headerHint ? 'margin-bottom' : 'margin-top'}: 8px;
    }

    ${Error} + ${Hint} {
      margin-top: 4px;
    }

    ${Error} {
      margin-top: 8px;
    }

    a {
      color: ${_color};
    }
  `}
`;
